// State handlers (§10.2 RECOVER and the rest of §2.14): what a bot does with the stick and buttons
// in each bean state that is not plain NORMAL play. TEETER pushes inward after the reaction delay
// (or freezes and windmills, for flavor), LEDGE waits and climbs (Spicy and Tactician bots play
// the ledge mind game against stompers), GRABBED mashes, TUMBLE steers with DI, FALLING throws the
// plunger save, CARRYING walks the victim to the edge and throws, SPUN aims the fling.
import { NO_SLOT, TUNE } from '../constants';
import { BS, ItemKind, MapId, ModeId } from '../ids';
import { canUseItem } from '../sim/stateTable';
import { chance, randIntRange, randRange } from '../rng';
import type { Vec2 } from '../types';
import { carryBomb } from './itemTactics';
import { crownLanding } from './modeTactics';
import type { Ctx } from './context';
import { wantDir, wantPoint } from './motor';
import { findEntity, hittable, isEnemy, nearestEnemy, pushDir, shoveAt } from './tactics';
import { BOT_TUNE } from './tuning';
import { dcos, dsin } from '../sim/dmath';

const F = BOT_TUNE.feel;
const R = TUNE.bot.recover;
const G: Vec2 = { x: 0, y: 0 };
const P: Vec2 = { x: 0, y: 0 };
const ARM = { t0: 0, t1: 0, dir: 1 };

/** Ticks of reaction for the bot's own state changes (the room's adaptive multiplier included). */
function reaction(ctx: Ctx): number {
  return Math.round(ctx.brain.p.reactionTicks * ctx.bc.reactionMult);
}

/** Called once when the bean's state changes: rolls the per-state plan. */
export function enterState(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  const w = ctx.w;
  switch (b.state) {
    case BS.TEETER:
      br.teeterSave = chance(br, br.p.teeterSave);
      br.teeterHop = chance(br, F.teeterHopChance[br.p.difficulty]);
      br.teeterAt = reaction(ctx) + randIntRange(br, 0, F.teeterJitterTicks);
      if (br.teeterSave) ctx.tele.teeterSaves++;
      else ctx.tele.teeterFreezes++;
      break;
    case BS.LEDGE:
      // Crumble Cake: the tile being held is on a timer; get up as soon as the hands react.
      br.ledgeClimbAt = w.cfg.mapId === MapId.CAKE
        ? w.tick + reaction(ctx) + randIntRange(br, 0, F.cakeLedgeJitterTicks)
        : w.tick + randIntRange(br, R.ledgeWaitMinTicks, R.ledgeWaitMaxTicks);
      br.ledgeMindGame = w.cfg.mapId !== MapId.CAKE && chance(br, br.p.ledgeMindGame);
      br.ledgePatient = w.cfg.mapId !== MapId.CAKE && chance(br, F.ledgePatience[br.p.difficulty]);
      br.ledgeWaiting = false;
      br.stomperTicks = 0;
      break;
    case BS.GRABBED:
      br.mashNext = w.tick + reaction(ctx);
      break;
    case BS.TUMBLE:
      br.diSide = chance(br, br.p.di) ? 1 : 0;
      break;
    case BS.CARRYING:
      br.carryTick = w.tick;
      break;
    default:
      break;
  }
  if (b.state !== BS.LEDGE) br.ledgeWaiting = false;
  br.recovering = false;
}

export function teeter(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  const n = b.edgeNormal;
  if (!br.teeterSave) {
    // Frozen in panic: windmill the stick along the rim (never inward enough to recover).
    const s = dsin(b.st * F.windmillRate);
    wantDir(ctx, -n.y * s, n.x * s, F.windmillSpeed);
    br.want.urgent = true;
    br.want.precise = true;
    return;
  }
  if (b.st < br.teeterAt) {
    br.want.precise = true;
    return;
  }
  if (br.teeterHop) {
    br.press.jump = true;
    br.teeterHop = false;
    return;
  }
  wantDir(ctx, n.x, n.y, 1);
  br.want.urgent = true;
  br.want.precise = true;
}

/** An enemy grounded within the stomper range of our edge point and closing on it, or NO_SLOT. */
function stomper(ctx: Ctx): number {
  const b = ctx.self;
  const ex = b.edgePoint.x;
  const ey = b.edgePoint.y;
  let best = NO_SLOT;
  let bd: number = R.stomperRange;
  for (let s = 0; s < ctx.seen.length; s++) {
    if (s === b.slot || !isEnemy(ctx, s)) continue;
    const o = ctx.seen[s];
    if (!o.body || o.state === BS.LEDGE || o.state === BS.TEETER || o.z > TUNE.bean.airborneZ + ctx.w.beans[s].groundZ) continue;
    const dx = ex - o.x;
    const dy = ey - o.y;
    const d = Math.hypot(dx, dy);
    if (d > bd) continue;
    const closing = d > 1e-6 ? (o.vx * dx + o.vy * dy) / d : 1;
    if (closing < F.stomperClosing && d > TUNE.edge.stompRadius + F.mindGameTrigger) continue;
    bd = d;
    best = s;
  }
  return best;
}

/** An enemy on its feet within `lurkRange` of the spot a climb comes up at (edge point + n·0.55), or NO_SLOT. */
function lurker(ctx: Ctx): number {
  const b = ctx.self;
  const ux = b.edgePoint.x + b.edgeNormal.x * TUNE.edge.climbInset;
  const uy = b.edgePoint.y + b.edgeNormal.y * TUNE.edge.climbInset;
  let best = NO_SLOT;
  let bd: number = F.lurkRange;
  for (let s = 0; s < ctx.seen.length; s++) {
    if (s === b.slot || !isEnemy(ctx, s)) continue;
    const o = ctx.seen[s];
    if (!o.body || (o.state !== BS.NORMAL && o.state !== BS.PUNCH_WINDUP)) continue;
    const d = Math.hypot(ux - o.x, uy - o.y);
    if (d < bd) {
      bd = d;
      best = s;
    }
  }
  return best;
}

export function ledge(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  const w = ctx.w;
  br.want.precise = true;
  br.waiting = true;
  const st = stomper(ctx);
  // Never hang until the timeout.
  const late = b.st >= TUNE.edge.ledgeTicks - F.ledgeTimeoutPad;
  // Spin Cycle: an arm sweeping the rim knocks hangers off; get up before it comes round.
  // Spin Cycle: an arm sweeping the rim knocks hangers (and climbers) off. Get up while the next
  // arm is still far enough away for the climb to finish; once it is too close, hang on and pray.
  if (ctx.hz.armCount > 0 && ctx.hz.sweeperWindow(b.pos.x, b.pos.y, b.radius, 0, 0, ARM)) {
    if (ARM.t0 > TUNE.edge.climbTicks + F.ledgeArmSlack && b.st >= Math.min(br.p.reactionTicks, F.ledgeArmLead)) climb(ctx);
    return;
  }
  if (st !== NO_SLOT && (br.ledgeMindGame || (br.ledgePatient && br.ledgeWaiting)) && !late) {
    // The mind game: hang on until the stomper is about to step on the hands, then pop up.
    const o = ctx.seen[st];
    const d = Math.hypot(b.edgePoint.x - o.x, b.edgePoint.y - o.y);
    br.stomperTicks++;
    if (w.tick >= br.ledgeClimbAt) br.ledgeWaiting = true;
    if (d <= TUNE.edge.stompRadius + F.mindGameTrigger) {
      if (br.ledgeWaiting || br.stomperTicks >= F.mindGameMinWait) ctx.tele.mindGames++;
      climb(ctx);
    }
    return;
  }
  if (w.tick < br.ledgeClimbAt && !late) return;
  // Someone is waiting up there to boop us straight back off (the ledge is used up after this
  // climb): a patient hanger stays down until they leave or come for the hands.
  if (br.ledgePatient && !late && lurker(ctx) !== NO_SLOT) {
    br.ledgeWaiting = true;
    return;
  }
  climb(ctx);
}

function climb(ctx: Ctx): void {
  const br = ctx.brain;
  br.press.jump = true;
  br.ledgeClimbAt = 1e9;
  br.ledgeWaiting = false;
  br.waiting = false;
  ctx.tele.ledgeClimbs++;
}

/** GRABBED (§10.2): mash at the difficulty's rate, alternating stick flips and button presses. */
export function grabbed(ctx: Ctx): void {
  const br = ctx.brain;
  const w = ctx.w;
  br.want.precise = true;
  if (w.tick < br.mashNext) {
    wantDir(ctx, br.mashFlip, 0, 1);
    br.want.urgent = true;
    return;
  }
  br.mashNext = w.tick + Math.max(1, Math.round(br.p.mashInterval * randRange(br, F.mashJitterMin, F.mashJitterMax)));
  br.mashCount++;
  if ((br.mashCount & 1) === 0) {
    br.mashFlip = -br.mashFlip;
  } else {
    const k = (br.mashCount >> 1) % 3;
    if (k === 0) br.press.shove = true;
    else if (k === 1) br.press.jump = true;
    else br.press.grab = true;
  }
  wantDir(ctx, br.mashFlip, 0, 1);
  br.want.urgent = true;
}

/** TUMBLE (§10.2): DI perpendicular toward safety (Normal 50%, Spicy always); Balloon/Plunger to stay on. */
export function tumble(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  br.want.precise = true;
  const vx = b.vel.x;
  const vy = b.vel.y;
  const v = Math.hypot(vx, vy);
  if (v < 1e-3) return;
  // Will this slide carry us off? (ground decel; airborne beans slide further)
  const slide = (v * v) / (2 * TUNE.tumble.decel) + (b.z > TUNE.bean.airborneZ ? v * F.tumbleAirSec : 0);
  const landS = ctx.field.safety(b.pos.x + (vx / v) * slide, b.pos.y + (vy / v) * slide);
  // The Balloon is kept for its passive auto-save (§10.3); a Plunger can yank us back early.
  if (landS < F.tumbleSaveSafety && b.item === ItemKind.PLUNGER && canUseItem(b.state, b.item)) plungerSave(ctx);
  if (br.diSide === 0 || b.st < TUNE.tumble.noControlTicks) return;
  const px = -vy / v;
  const py = vx / v;
  const ahead = Math.min(slide, F.diProbe);
  const sa = ctx.field.safety(b.pos.x + (vx / v) * ahead + px * F.diSide, b.pos.y + (vy / v) * ahead + py * F.diSide);
  const sb = ctx.field.safety(b.pos.x + (vx / v) * ahead - px * F.diSide, b.pos.y + (vy / v) * ahead - py * F.diSide);
  let side = sa >= sb ? 1 : -1;
  // Crown Grab: knocked out from under the crown, steer the slide toward where it comes down
  // (as long as that side keeps us on the floor).
  const crown = ctx.w.cfg.modeId === ModeId.CROWN_GRAB && ctx.w.mode.crownHolder === NO_SLOT && ctx.w.tick - br.crownLostTick < BOT_TUNE.modes.crownDiTicks
    ? findEntity(ctx.w, ctx.w.mode.crownEntId) : null;
  if (crown && Math.min(sa, sb) > F.crownDiSafety) {
    crownLanding(ctx, crown, G);
    const toward = (G.x - b.pos.x) * px + (G.y - b.pos.y) * py;
    if (Math.abs(toward) > F.crownDiMin) side = toward > 0 ? 1 : -1;
  }
  wantDir(ctx, px * side, py * side, 1);
  br.want.urgent = true;
}

/** Plunger save (§3.3): fire at the bean that best pulls us back onto the arena. */
function plungerSave(ctx: Ctx): boolean {
  const br = ctx.brain;
  const b = ctx.self;
  let best = NO_SLOT;
  let bs = -1e9;
  for (let s = 0; s < ctx.seen.length; s++) {
    if (s === b.slot) continue;
    const o = ctx.seen[s];
    if (!o.body) continue;
    const d = Math.hypot(o.x - b.pos.x, o.y - b.pos.y);
    if (d > TUNE.plunger.speed * TUNE.plunger.lifeTicks / 60) continue;
    const score = ctx.field.safety(o.x, o.y) - d * F.plungerSaveDistWeight;
    if (score > bs) {
      bs = score;
      best = s;
    }
  }
  if (best === NO_SLOT) return false;
  const o = ctx.seen[best];
  const lead = br.p.lead * Math.hypot(o.x - b.pos.x, o.y - b.pos.y) / TUNE.plunger.speed;
  br.press.item = true;
  br.press.ax = o.x + o.vx * lead - b.pos.x;
  br.press.ay = o.y + o.vy * lead - b.pos.y;
  ctx.tele.itemPresses[ItemKind.PLUNGER]++;
  return true;
}

export function falling(ctx: Ctx): void {
  const b = ctx.self;
  ctx.brain.want.precise = true;
  if (b.item === ItemKind.PLUNGER && b.z > TUNE.plunger.fallingMinZ && b.z < F.plungerSaveBelowZ) plungerSave(ctx);
}

/** FLOAT: drift back over safe ground, away from jumping attackers. */
export function float(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  const s = ctx.field.safety(b.pos.x, b.pos.y);
  ctx.field.gradient(b.pos.x, b.pos.y, G);
  if (s < ctx.margin + F.floatExtraSafety && Math.hypot(G.x, G.y) > 1e-6) {
    wantDir(ctx, G.x, G.y, 1);
    br.want.margin = 0;
    br.want.ignoreDanger = true;
    return;
  }
  wantPoint(ctx, ctx.cx, ctx.cy, F.floatDriftSpeed);
  br.want.margin = 0;
}

/**
 * RESPAWN parachute: steer toward (tx, ty) (Crown Grab: the crown), but land on floor. The
 * descent is short and slow (§4.3: 40 ticks at 50% speed, about 2 m of drift), so when the way to
 * the goal does not end on solid ground (a Crumble Cake parachute over a missing tile), drift to
 * the best ground in reach instead, nearest the goal.
 */
export function respawn(ctx: Ctx, tx: number, ty: number): void {
  const br = ctx.brain;
  const b = ctx.self;
  br.want.precise = true;
  br.want.margin = 0;
  br.want.ignoreDanger = true;
  if (b.respawnTicks > 0) {
    wantPoint(ctx, tx, ty, 1);
    return;
  }
  const perTick = TUNE.respawn.paraZ / TUNE.respawn.paraTicks;
  const ticksLeft = Math.max(0, b.z / perTick);
  const reach = TUNE.bean.runSpeed * TUNE.respawn.paraSpeed * (ticksLeft / 60) * F.paraReachFrac;
  const f = ctx.field;
  const dx = tx - b.pos.x;
  const dy = ty - b.pos.y;
  const d = Math.hypot(dx, dy);
  const go = Math.min(d, reach);
  const lx = d > 1e-6 ? b.pos.x + (dx / d) * go : b.pos.x;
  const ly = d > 1e-6 ? b.pos.y + (dy / d) * go : b.pos.y;
  if (f.safety(lx, ly) >= F.paraLandSafety) {
    wantPoint(ctx, tx, ty, 1);
    return;
  }
  let best = -1e9;
  let bx = b.pos.x;
  let by = b.pos.y;
  for (let ri = 0; ri <= F.paraRings; ri++) {
    const r = (reach * ri) / F.paraRings;
    const n = ri === 0 ? 1 : F.paraDirs;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const x = b.pos.x + dcos(a) * r;
      const y = b.pos.y + dsin(a) * r;
      const s = f.safety(x, y);
      const score = Math.min(s, F.paraSafetyCap) * (s > 0 ? 1 : F.paraVoidWeight) - Math.hypot(x - tx, y - ty) * F.paraGoalWeight;
      if (score > best) {
        best = score;
        bx = x;
        by = y;
      }
    }
  }
  wantPoint(ctx, bx, by, 1);
  br.want.arrive = 0.1;
}

/** CARRYING (§10.2): carry along the push direction and throw within 2 m of the edge. */
export function carrying(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  const w = ctx.w;
  const victim = b.partner;
  if (victim === NO_SLOT) {
    carryBomb(ctx);
    return;
  }
  const s = ctx.field.safety(b.pos.x, b.pos.y);
  // Push direction from the carrier's own spot (the victim hangs in front of it).
  pushDir(ctx, b.pos.x, b.pos.y, ctx.cx, ctx.cy, P);
  const held = w.tick - br.carryTick;
  const threatened = nearestEnemy(ctx, F.carryThreatRange) !== NO_SLOT && nearestEnemy(ctx, F.carryThreatRange) !== victim;
  if (s < TUNE.bot.flank.throwEdgeDist || held >= F.carryMaxTicks || threatened) {
    br.press.grab = true;
    br.press.ax = P.x;
    br.press.ay = P.y;
    ctx.tele.throws++;
    return;
  }
  wantDir(ctx, P.x, P.y, 1);
  br.want.margin = TUNE.bot.flank.throwEdgeDist * F.carryMarginFrac;
  br.want.precise = true;
}

/** SPUN (the teacup cup): pick the fling direction (Chaos aims at someone, others at open ground). */
export function spun(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  if (b.st < F.flingAimFromTicks) return;
  if (br.p.crowdBombs) {
    const s = nearestEnemy(ctx, F.flingTargetRange);
    if (s !== NO_SLOT) {
      wantDir(ctx, ctx.seen[s].x - b.pos.x, ctx.seen[s].y - b.pos.y, 1);
      return;
    }
  }
  let bestA = 0;
  let best = -1;
  for (let i = 0; i < F.flingDirs; i++) {
    const a = (i / F.flingDirs) * Math.PI * 2;
    const d = ctx.field.voidRayDist(ctx.w, b.pos.x, b.pos.y, dcos(a), dsin(a));
    if (d > best) {
      best = d;
      bestA = a;
    }
  }
  wantDir(ctx, dcos(bestA), dsin(bestA), 1);
}

/** PUNCH_WINDUP: keep turning toward the target (facing turns at 540°/s) with a wobbly aim. */
export function windup(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  const slot = br.target !== NO_SLOT ? br.target : nearestEnemy(ctx, F.gloveTrackRange);
  if (slot === NO_SLOT) return;
  const o = ctx.seen[slot];
  wantDir(ctx, o.x - b.pos.x, o.y - b.pos.y, F.windupStick);
  br.want.precise = true;
}

/**
 * Right after a climb the ledge is used up (§2.9: for 5 s a knock-off is a fall, not a hang), and
 * the climb leaves the bean 0.55 m from the rim. A person scrambles inward before doing anything
 * else: run up the safety gradient until `climbRetreatSafety` of floor is behind us (capped on
 * small ground, like the edge margin) or `climbRetreatTicks` have passed. Reflexes (clash, dodge)
 * and the climb payoff still come first.
 */
export function climbRetreat(ctx: Ctx): boolean {
  const br = ctx.brain;
  const b = ctx.self;
  if (b.state !== BS.NORMAL || br.prevState !== BS.CLIMB || b.cdLedge === 0 || ctx.w.tick - br.stateTick > F.climbRetreatTicks) return false;
  const want = Math.min(F.climbRetreatSafety, Math.max(BOT_TUNE.marginFloor, ctx.field.maxSafety * BOT_TUNE.marginCapFrac));
  if (ctx.field.safety(b.pos.x, b.pos.y) >= want) return false;
  ctx.field.gradient(b.pos.x, b.pos.y, G);
  if (Math.hypot(G.x, G.y) < 1e-6) {
    G.x = ctx.cx - b.pos.x;
    G.y = ctx.cy - b.pos.y;
  }
  wantDir(ctx, G.x, G.y, 1);
  br.want.urgent = true;
  br.want.margin = want;
  ctx.tele.climbRetreatTicks++;
  return true;
}

/** Right after climbing up, a stomper standing on the rim is the perfect victim (mind-game payoff). */
export function climbPayoff(ctx: Ctx): boolean {
  const br = ctx.brain;
  const b = ctx.self;
  if (b.state !== BS.NORMAL || b.cdShove !== 0 || ctx.w.tick - br.stateTick > F.payoffTicks || br.prevState !== BS.CLIMB) return false;
  const s = nearestEnemy(ctx, F.payoffRange);
  if (s === NO_SLOT || !hittable(ctx, s)) return false;
  const o = ctx.seen[s];
  if (ctx.field.safety(o.x, o.y) > F.payoffVictimSafety) return false;
  shoveAt(ctx, o, o.x, o.y);
  return true;
}


